/**
 * Play-mode live world helpers (pure): the running entities as hierarchy
 * rows, and "keep play-mode changes" — turning what the user chose to keep
 * from the running game into ordinary scene edits (one undo step) once play
 * stops.
 *
 * Entities inside prefab instances (`"<instance>/<child>"`) need no special
 * case: scene commands on them become prefab overrides (see scene-doc.ts).
 */

import type { LiveEntity, LiveTree, LiveTreeEntity } from '../engine/prism.js';
import type { EditorCommand, EntityDesc, SceneDoc } from './scene-doc.js';
import { prefabInstanceOf } from './prefab.js';

export interface LiveRow {
  entity: LiveTreeEntity;
  depth: number;
  hasChildren: boolean;
}

/** Parent-before-child rows of the running world (like `hierarchyRows`). */
export function liveRows(tree: LiveTree | null, collapsed: ReadonlySet<string> = new Set(), filter = ''): LiveRow[] {
  if (!tree) return [];
  const ids = new Set(tree.entities.map((e) => e.id));
  const children = new Map<string | null, LiveTreeEntity[]>();
  for (const e of tree.entities) {
    const key = e.parent && ids.has(e.parent) && e.parent !== e.id ? e.parent : null;
    const bucket = children.get(key) ?? [];
    bucket.push(e);
    children.set(key, bucket);
  }
  const needle = filter.trim().toLowerCase();
  if (needle) {
    return tree.entities
      .filter((e) => e.name.toLowerCase().includes(needle) || e.id.toLowerCase().includes(needle))
      .map((entity) => ({ entity, depth: 0, hasChildren: false }));
  }
  const out: LiveRow[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const entity of children.get(parent) ?? []) {
      if (seen.has(entity.id)) continue;
      seen.add(entity.id);
      const kids = children.get(entity.id) ?? [];
      out.push({ entity, depth, hasChildren: kids.length > 0 });
      if (!collapsed.has(entity.id)) walk(entity.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** What to keep of one running entity: all of it, its transform or one component. */
export type KeepPart = 'entity' | 'transform' | `component:${string}`;

export interface KeepItem {
  id: string;
  part: KeepPart;
}

export const keepKey = (item: KeepItem) => `${item.id}|${item.part}`;

export function describeKeep(item: KeepItem, names: ReadonlyMap<string, string> = new Map()): string {
  const name = names.get(item.id) ?? item.id;
  if (item.part === 'entity') return name;
  if (item.part === 'transform') return `${name} · Transform`;
  return `${name} · ${item.part.slice('component:'.length)}`;
}

/** Can this entity be written to the scene? (Not additive-scene or stream entities.) */
export function keepable(doc: SceneDoc, id: string): boolean {
  if (id.startsWith('@scene:')) return false;
  return doc.entities.some((e) => e.id === id) || prefabInstanceOf(doc, id) !== null || !id.includes('/');
}

/**
 * Scene commands that write the kept parts of the running world (snapshots
 * taken just before stopping) into the scene document. Entities that only
 * existed in play (spawned by scripts) are added; parts of entities that are
 * gone are skipped (reported in `skipped`).
 */
export function keepCommands(
  doc: SceneDoc,
  snapshots: ReadonlyMap<string, LiveEntity | null>,
  items: readonly KeepItem[],
): { commands: EditorCommand[]; skipped: KeepItem[] } {
  const commands: EditorCommand[] = [];
  const skipped: KeepItem[] = [];
  const inDoc = (id: string) => doc.entities.find((e) => e.id === id) ?? null;
  const added = new Set<string>();
  // Whole entities first, parents before children.
  const ordered = [...items].sort((a, b) => depthOf(a.id) - depthOf(b.id));
  for (const item of ordered) {
    const live = snapshots.get(item.id) ?? null;
    if (!live || !keepable(doc, item.id)) {
      skipped.push(item);
      continue;
    }
    const existing = inDoc(item.id);
    const virtual = !existing && prefabInstanceOf(doc, item.id) !== null;
    if (!existing && !virtual) {
      // Spawned during play: add it whole (once), whatever part was asked for.
      if (added.has(item.id)) continue;
      added.add(item.id);
      const parent = live.parent && (inDoc(live.parent) || added.has(live.parent) || prefabInstanceOf(doc, live.parent)) ? live.parent : null;
      const desc: EntityDesc = { id: item.id, name: live.name, parent, transform: live.transform, components: stripRuntimeOnly(live.components) };
      commands.push({ cmd: 'addEntity', desc, parent });
      continue;
    }
    if (item.part === 'entity' || item.part === 'transform') {
      commands.push({ cmd: 'setTransform', id: item.id, transform: live.transform });
    }
    if (item.part === 'entity') {
      const currentName = existing?.name ?? null;
      if (live.name && live.name !== currentName && live.name !== item.id) commands.push({ cmd: 'renameEntity', id: item.id, name: live.name });
      const keys = new Set([...Object.keys(existing?.components ?? {}), ...Object.keys(live.components)]);
      for (const key of keys) {
        // Components a prefab root added to its instance are not the instance's own.
        if (existing?.components?.prefab && !(key in (existing.components ?? {}))) continue;
        commands.push(...componentCommands(item.id, key, live, existing));
      }
    } else if (item.part.startsWith('component:')) {
      commands.push(...componentCommands(item.id, item.part.slice('component:'.length), live, existing));
    }
  }
  return { commands, skipped };
}

function componentCommands(id: string, key: string, live: LiveEntity, existing: EntityDesc | null): EditorCommand[] {
  const value = live.components[key];
  if (value === undefined || value === null) {
    return existing && existing.components && key in existing.components ? [{ cmd: 'removeComponent', id, key }] : [];
  }
  if (existing?.components && sameJson(existing.components[key], value)) return [];
  return [{ cmd: 'setComponent', id, key, value }];
}

/** Keys the engine serializes that are not scene components. */
function stripRuntimeOnly(components: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(components)) if (v !== null && v !== undefined) out[k] = v;
  return out;
}

function depthOf(id: string): number {
  return id.split('/').length;
}

/** JSON equality with numbers compared at f32 precision (engine round trips). */
export function sameJson(a: unknown, b: unknown): boolean {
  const norm = (v: unknown) => JSON.stringify(v, (_k, x: unknown) => (typeof x === 'number' ? Number(x.toPrecision(6)) : x));
  return norm(a) === norm(b);
}
